import { AppIcon } from '@/components/ui/app-icon';
import { AppLayout } from '@/components/app-layout';
import { AppSumoHomeShowcase } from '@/components/appsumo/appsumo-home-showcase.component';
import { AuthRouter } from '@/components/auth/auth-router.component';
import { Head } from '@/components/head.component';
import { OnboardingCheck } from '@/components/onboarding-check.component';
import { ProspectContainer } from '@/containers/root/prospect-container.component';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { useGetPlansQuery } from '@/store/features/billing.feature';
import { Show } from '@clerk/react';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense, lazy } from 'react';
import { iconSpinnerThird } from '@/lib/icons';

export const Route = createFileRoute('/')({
  component: Index,
});

// Lazy-loaded so the home route's critical bundle never pulls the copilot graph
// (Lexical + assistant-ui + Framer + tool-UIs) for users who fail-close to the
// legacy home — notably AppSumo-only orgs, who never render CopilotHome.
const CopilotHome = lazy(() =>
  import('@/containers/root/copilot-home.component').then((m) => ({
    default: m.CopilotHome,
  }))
);

/**
 * Decides what the home page (`/`) renders — **fail-closed**:
 *
 *   - access/plan **error** → the legacy prospecting home (`ProspectContainer`).
 *     The Copilot surface is net-new and totally unavailable to AppSumo orgs;
 *     on error we can't prove an org isn't AppSumo, so we NEVER render the new
 *     surface (mirrors the backend `/agent/chat` 403 backstop).
 *   - **AppSumo-only** → the legacy prospecting home, under the AppSumo
 *     upgrade showcase while that price is open (it renders nothing else).
 *   - everyone else (resolved, non-AppSumo) → `CopilotHome`. Home is always
 *     the home: new signups reach Clu's first chat from onboarding itself, and
 *     a workspace without a company profile is never sent away from here.
 *
 * Holds a neutral skeleton until access resolves so neither home flashes
 * before the decision. Reads `useFeatureAccess` (NOT `useCopilotAccess`,
 * which fails OPEN during plan load).
 */
function CopilotSurfaceGate() {
  const { isReady, isAppSumoOnly, error, organizationId } = useFeatureAccess();
  // isAppSumoOnly derives from the plans query, which loads INDEPENDENTLY of
  // useFeatureAccess's `isReady` (that only tracks the feature-access query).
  // Gate on plansLoading too, or there's a fail-OPEN window where isReady=true
  // but isAppSumoOnly is still false → an AppSumo org would briefly render the
  // net-new surface. Mirrors AppSumoCopilotGate.
  // Capture isError too: a plans-query error (independent of the feature-access
  // `error`) leaves isAppSumoOnly=false with no plan data — a fail-OPEN. Treat
  // it as fail-closed to the legacy home.
  const { isLoading: plansLoading, isError: plansError } = useGetPlansQuery(
    { organizationId },
    { skip: !organizationId }
  );

  if (error || plansError) return <ProspectContainer />; // fail closed → legacy
  if (!isReady || plansLoading) {
    return <CopilotHomeSkeleton />;
  }
  if (isAppSumoOnly) {
    // Fenced → legacy home, with what Pro adds above it while the AppSumo
    // upgrade price is open.
    return (
      <>
        <AppSumoHomeShowcase />
        <ProspectContainer />
      </>
    );
  }
  return (
    <Suspense fallback={<CopilotHomeSkeleton />}>
      <CopilotHome />
    </Suspense>
  );
}

function CopilotHomeSkeleton() {
  return (
    <div className='flex h-full min-h-0 w-full items-center justify-center'>
      <AppIcon
        icon={iconSpinnerThird}
        className='animate-spin text-muted-foreground'
        style={{ fontSize: 20 }}
      />
    </div>
  );
}

function Index() {
  return (
    <>
      <Head heading='Home' />
      <Show when='signed-in' fallback={<AuthRouter />}>
        <OnboardingCheck>
          <AppLayout variant='full-bleed'>
            <CopilotSurfaceGate />
          </AppLayout>
        </OnboardingCheck>
      </Show>
    </>
  );
}

export default Index;
